
import { action, flow, observable } from 'mobx'
import { ApiError, apiResponse, apiState } from '../../Api'
import { AlunoModel } from '../Aluno'
import { RootStore } from '../createStore'
import { ValidationError } from '../IValidator'
import { ProfessorModel } from '../Professor'
import {
    enviarFormulario,
    editarFormulario,
    buscarPreProjetoDoAlunoAtual,
    getParamsPreProjeto,
    buscarTodosPreProjetosFuncionario,
    getListaAlunosOrientacao,
    recuperarInformacoesPreProjeto,
    ppAprovar,
    ppReprovar,
    getVagasRestantesProfessor,
    recuperarInformacoesPreProjetoFuncionario,
    ppAprovarFuncionario,
    recuperarListaDeParametros,
    criarNovoParametro,
    editarParametro,
    getParamsPreProjetoPeloId
} from './Service'
import { ParametroPreProjetoModel, PreProjetoModel, SituacaoDoPreProjeto, OrientadorModel } from './Model'


export class PreProjetoStore {

    /*
        Properties
    */
    @observable public tema: string
    @observable public titulo: string
    @observable public justificativa: string
    @observable public desenvolvimento: string
    @observable public bibliografia: string
    @observable public orientadores: Array<ProfessorModel> = observable([
        new ProfessorModel(),
        new ProfessorModel(),
        new ProfessorModel()
    ])
    @observable public status: SituacaoDoPreProjeto
    @observable public aluno: AlunoModel

    @observable public preProjeto: PreProjetoModel
    @observable public orientacao: OrientadorModel
    @observable public orientador: ProfessorModel

    @observable public preProjetosAprovadosPendentes: PreProjetoModel[]
    @observable public preProjetosRecusados: PreProjetoModel[]
    @observable public preProjetoList: Array<OrientadorModel> = [];

    @observable public vagasRestantes: number
    @observable public periodoDeAvaliacao: boolean = false
    @observable public parametros: ParametroPreProjetoModel
    @observable public parametrosList: ParametroPreProjetoModel[]
    @observable carregouTodosPreProjetos: boolean = false;
    @observable faseAtual: number

    @observable preProjetosListQuery = {
        search: '',
        page: 1,
        pageSize: 10
    }

    @observable preProjetosRecusadosListQuery = {
        searchRecusados: '',
        pageRecusados: 1,
        pageSizeRecusados: 10
    }

    @observable public justificativaReprovacao: string;
    @observable public justificativaReprovacaoValidationError: ValidationError<{ justificativaReprovacao: string }>;

    @observable preProjetoState: apiState = apiState.loading
    @observable preProjetoListState: apiState = apiState.loading
    @observable parametrosListState: apiState = apiState.loading
    @observable preProjetoListStateRecusados: apiState = apiState.loading
    @observable orientandosState: apiState = apiState.loading

    /*
        Validators
    */
    @observable public preProjetoValidationError: ValidationError<{
        tema: string,
        titulo: string,
        justificativa: string,
        desenvolvimento: string,
        bibliografia: string,
    }>

    @observable public parametrosPreProjetoValidationError: ValidationError<{
        serie: number,
        dataDeInicio: string, horaDeInicio: string,
        dataDeFim: string, horaDeFim: string,
        dataDeInicioInscricoes: string, horaDeInicioInscricoes: string,
        dataDeFimInscricoes: string, horaDeFimInscricoes: string,
        dataDeInicioPF: string, horaDeInicioPF: string,
        dataDeFimPF: string, horaDeFimPF: string,
        dataDeInicioSF: string, horaDeInicioSF: string,
        dataDeFimSF: string, horaDeFimSF: string,
        dataDeInicioTF: string, horaDeInicioTF: string,
        dataDeFimTF: string, horaDeFimTF: string
    }>

    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action
    setJustificativaError = (validationError: ValidationError<{ justificativaReprovacao: string }>) => {
        this.justificativaReprovacaoValidationError = validationError;
    }

    @action
    setValidationError = (
        validationError: ValidationError<{
            tema: string,
            titulo: string,
            justificativa: string,
            desenvolvimento: string,
            bibliografia: string,
        }>) => {
        this.preProjetoValidationError = validationError
    }

    @action
    setParametrosError = (
        validationError: ValidationError<{
            serie: number,
            dataDeInicio: string, horaDeInicio: string,
            dataDeFim: string, horaDeFim: string,
            dataDeInicioInscricoes: string, horaDeInicioInscricoes: string,
            dataDeFimInscricoes: string, horaDeFimInscricoes: string,
            dataDeInicioPF: string, horaDeInicioPF: string,
            dataDeFimPF: string, horaDeFimPF: string,
            dataDeInicioSF: string, horaDeInicioSF: string,
            dataDeFimSF: string, horaDeFimSF: string,
            dataDeInicioTF: string, horaDeInicioTF: string,
            dataDeFimTF: string, horaDeFimTF: string
        }>) => {
        this.parametrosPreProjetoValidationError = validationError
    }

    @action
    updateAttribute(name: string, value: any) {
        this[name] = value
    }

    @action
    updateObject = (object: string, field: string, value: any) => {
        this[object][field] = value;
    };

    @action
    updateArray = (arrayName: string, index: number, value: any) => {
        this[arrayName][index] = value;
    }

    @action
    updateField = (name: string, value: any) => {
        this[name] = value;
    }

    enviarFormulario = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const preProjeto = {
            tema: this.tema,
            titulo: this.titulo,
            justificativa: this.justificativa,
            desenvolvimento: this.desenvolvimento,
            bibliografia: this.bibliografia,
            professorId: this.orientadores.map(o => o.id)
        }

        const response: apiResponse<ApiError[]> = yield enviarFormulario(preProjeto)

        this.preProjetoState = response.status

        if (response.status === apiState.error || response.status === apiState.badRequest)
            this.preProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
    })

    editarFormulario = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const preProjeto = {
            tema: this.tema,
            titulo: this.titulo,
            justificativa: this.justificativa,
            desenvolvimento: this.desenvolvimento,
            bibliografia: this.bibliografia,
            professorId: this.orientadores.map(o => o.id)
        }

        const response: apiResponse<ApiError[]> = yield editarFormulario(preProjeto)

        this.preProjetoState = response.status

        if (response.status === apiState.error)
            this.preProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
    })

    buscarPreProjetoDoAlunoAtual = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const proj: apiResponse<PreProjetoModel> = yield buscarPreProjetoDoAlunoAtual()

        this.preProjeto = proj.data

        if (this.preProjeto) {
            this.tema = this.preProjeto.tema
            this.titulo = this.preProjeto.titulo
            this.justificativa = this.preProjeto.justificativa
            this.desenvolvimento = this.preProjeto.desenvolvimento
            this.bibliografia = this.preProjeto.bibliografia
            this.orientadores = this.preProjeto.orientadores!
            .sort((a,b)=>a.ordemParaConvite - b.ordemParaConvite)
            .map(p => p.professor)
        }

        this.preProjetoState = proj.status == apiState.notFound ? apiState.done : proj.status
    })

    getParamsPreProjeto = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const params: apiResponse<ParametroPreProjetoModel> = yield getParamsPreProjeto()

        this.parametros = params.data
        this.preProjetoState = params.status
    })

    getParamsPreProjetoPeloId = flow(function* (this: PreProjetoStore, parametroId: number) {
        this.preProjetoState = apiState.loading

        if (!isNaN(parametroId)) {
            const params: apiResponse<ParametroPreProjetoModel> = yield getParamsPreProjetoPeloId(parametroId)

            this.parametros = params.data
            this.preProjetoState = params.status
        } else {
            this.preProjetoState = apiState.done
        }
    })

    buscarListaDeParametros = flow(function* (this: PreProjetoStore) {
        this.parametrosListState = apiState.loading

        const list = yield recuperarListaDeParametros()

        this.parametrosList = list.data
        this.parametrosListState = list.status
    })

    criarNovoParametro = flow(function* (
        this: PreProjetoStore, serie: string,
        dataDeInicio: string, horaDeInicio: string,
        dataDeFim: string, horaDeFim: string,
        dataDeInicioInscricoes: string, horaDeInicioInscricoes: string,
        dataDeFimInscricoes: string, horaDeFimInscricoes: string,
        dataDeInicioPF: string, horaDeInicioPF: string,
        dataDeFimPF: string, horaDeFimPF: string,
        dataDeInicioSF: string, horaDeInicioSF: string,
        dataDeFimSF: string, horaDeFimSF: string,
        dataDeInicioTF: string, horaDeInicioTF: string,
        dataDeFimTF: string, horaDeFimTF: string,
        observacao: string
    ) {
        this.preProjetoState = apiState.loading

        const response: apiResponse<ApiError[]> = yield criarNovoParametro(
            serie,
            dataDeInicio, horaDeInicio,
            dataDeFim, horaDeFim,
            dataDeInicioInscricoes, horaDeInicioInscricoes,
            dataDeFimInscricoes, horaDeFimInscricoes,
            dataDeInicioPF, horaDeInicioPF,
            dataDeFimPF, horaDeFimPF,
            dataDeInicioSF, horaDeInicioSF,
            dataDeFimSF, horaDeFimSF,
            dataDeInicioTF, horaDeInicioTF,
            dataDeFimTF, horaDeFimTF,
            observacao
        )
        this.preProjetoState = response.status

        if (response.status === apiState.error || response.status === apiState.badRequest)
            this.parametrosPreProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
    })

    editarParametro = flow(function* (
        this: PreProjetoStore, id: number, serie: string,
        dataDeInicio: string, horaDeInicio: string,
        dataDeFim: string, horaDeFim: string,
        dataDeInicioInscricoes: string, horaDeInicioInscricoes: string,
        dataDeFimInscricoes: string, horaDeFimInscricoes: string,
        dataDeInicioPF: string, horaDeInicioPF: string,
        dataDeFimPF: string, horaDeFimPF: string,
        dataDeInicioSF: string, horaDeInicioSF: string,
        dataDeFimSF: string, horaDeFimSF: string,
        dataDeInicioTF: string, horaDeInicioTF: string,
        dataDeFimTF: string, horaDeFimTF: string,
        observacao: string
    ) {
        this.preProjetoState = apiState.loading

        const response: apiResponse<ApiError[]> = yield editarParametro(
            id,
            serie,
            dataDeInicio, horaDeInicio,
            dataDeFim, horaDeFim,
            dataDeInicioInscricoes, horaDeInicioInscricoes,
            dataDeFimInscricoes, horaDeFimInscricoes,
            dataDeInicioPF, horaDeInicioPF,
            dataDeFimPF, horaDeFimPF,
            dataDeInicioSF, horaDeInicioSF,
            dataDeFimSF, horaDeFimSF,
            dataDeInicioTF, horaDeInicioTF,
            dataDeFimTF, horaDeFimTF,
            observacao
        )

        this.preProjetoState = response.status

        if (response.status === apiState.error || response.status === apiState.badRequest)
            this.parametrosPreProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
    })

    verificarSePeriodoDeAvaliacao = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const params: apiResponse<ParametroPreProjetoModel> = yield getParamsPreProjeto()
        this.parametros = params.data

        const hoje = new Date()
        const inicio = new Date(this.parametros && this.parametros.dataDeInicioPF)
        const fim = new Date(this.parametros && this.parametros.dataDeFim)

        if (hoje >= inicio && hoje <= fim) {
            this.periodoDeAvaliacao = true
        }
        this.preProjetoState = params.status
    })

    getFaseAtual = flow(function* (this: PreProjetoStore) {
        this.preProjetoState = apiState.loading

        const params: apiResponse<ParametroPreProjetoModel> = yield getParamsPreProjeto()
        this.parametros = params.data

        const hoje = new Date()

        if (this.parametros) {

            if (hoje >= new Date(this.parametros.dataDeInicioPF) && hoje <= new Date(this.parametros.dataDeFimPF)) {
                this.faseAtual = 1;
            }
            else if (hoje >= new Date(this.parametros.dataDeInicioSF) && hoje <= new Date(this.parametros.dataDeFimSF)) {
                this.faseAtual = 2;
            }
            else if (hoje >= new Date(this.parametros.dataDeInicioTF) && hoje <= new Date(this.parametros.dataDeFimTF)) {
                this.faseAtual = 3;
            }
        }

        this.preProjetoState = params.status
    })

    getListaAlunosOrientacao = flow(function* (this: PreProjetoStore, concat: boolean = false) {
        this.orientandosState = apiState.loading;

        const preProjetoList: apiResponse<OrientadorModel[]> = yield getListaAlunosOrientacao();

        this.preProjetoList = concat
            ? this.preProjetoList.concat(preProjetoList.data)
            : preProjetoList.data

        this.orientandosState = preProjetoList.status;

        this.orientandosState = preProjetoList.status
    })

    recuperarInformacoesPreProjeto = flow(function* (
        this: PreProjetoStore, preProjetoId: number) {
        this.orientandosState = apiState.loading;

        const response: apiResponse<OrientadorModel> = yield recuperarInformacoesPreProjeto(
            preProjetoId
        );
        this.orientacao = response.data;

        if (this.orientacao) {
            this.preProjeto = this.orientacao.preProjeto
            this.getVagasRestantesProfessor(this.orientacao.professorId);
        }

        this.orientandosState = response.status
    })

    ppAprovar = flow(function* (this: PreProjetoStore) {
        const orientacao = this.orientacao;

        if (this.orientacao) {
            this.preProjeto = this.orientacao.preProjeto
        }

        if (orientacao) {
            const response: apiResponse<ApiError[]> = yield ppAprovar(orientacao.preProjeto.id);

            if (response.status === apiState.done) {
                this.orientacao.conviteAceito = true;
                this.orientacao.preProjeto.situacao = SituacaoDoPreProjeto.aprovado
            }

            if (response.status === apiState.error || response.status === apiState.badRequest)
                this.preProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
        }

        this.preProjeto && this.recuperarInformacoesPreProjeto(this.preProjeto.id);
    })

    ppReprovar = flow(function* (this: PreProjetoStore) {
        const orientacao = this.orientacao;

        if (this.orientacao) {
            this.preProjeto = this.orientacao.preProjeto
        }

        if (orientacao) {
            const response: apiResponse<ApiError[]> = yield ppReprovar(orientacao.preProjeto.id, this.justificativaReprovacao);

            if (response.status === apiState.done) {
                this.orientacao.conviteAceito = false;
                this.orientacao.preProjeto.situacao = SituacaoDoPreProjeto.recusado
            }

            if (response.status === apiState.error || response.status === apiState.badRequest)
                this.preProjetoValidationError!.global = response.data!.flatMap(err => err.errorMessages);
        }

        this.preProjeto && this.recuperarInformacoesPreProjeto(this.preProjeto.id);
    })

    getVagasRestantesProfessor = flow(function* (this: PreProjetoStore, id: number) {
        this.orientandosState = apiState.loading

        const params: apiResponse<number> = yield getVagasRestantesProfessor(id)

        this.vagasRestantes = params.data
        this.orientandosState = params.status
    })

    buscarTodosPreProjetosFuncionario = flow(function* (
        this: PreProjetoStore,
        concat: boolean = false
    ) {
        this.preProjetoListState = apiState.loading

        const { page, pageSize, search } = this.preProjetosListQuery

        const preProjetos = yield buscarTodosPreProjetosFuncionario(false, page, pageSize, search)

        this.preProjetosAprovadosPendentes = concat
            ? this.preProjetosAprovadosPendentes.concat(preProjetos.data)
            : preProjetos.data

        this.preProjetoListState = preProjetos.status
    })

    buscarTodosPreProjetosFuncionarioRecusados = flow(function* (
        this: PreProjetoStore,
        concat: boolean = false
    ) {
        this.preProjetoListStateRecusados = apiState.loading

        const { pageRecusados, pageSizeRecusados, searchRecusados } = this.preProjetosRecusadosListQuery

        const preProjetosRecusados = yield buscarTodosPreProjetosFuncionario(true, pageRecusados, pageSizeRecusados, searchRecusados)

        this.preProjetosRecusados = concat
            ? this.preProjetosRecusados.concat(preProjetosRecusados.data)
            : preProjetosRecusados.data

        this.preProjetoListStateRecusados = preProjetosRecusados.status
    })

    buscarPreProjetoFuncionario = flow(function* (
        this: PreProjetoStore,
        preProjetoId: number) {
        this.preProjetoState = apiState.loading

        const proj: apiResponse<PreProjetoModel> = yield recuperarInformacoesPreProjetoFuncionario(
            preProjetoId
        );

        this.preProjeto = proj.data

        if (this.preProjeto) {
            this.tema = this.preProjeto.tema
            this.titulo = this.preProjeto.titulo
            this.justificativa = this.preProjeto.justificativa
            this.desenvolvimento = this.preProjeto.desenvolvimento
            this.bibliografia = this.preProjeto.bibliografia
            this.orientadores = this.preProjeto.orientadores!.map(p => p.professor)
        }

        this.preProjetoState = proj.status
    })

    ppAprovarFuncionario = flow(function* (this: PreProjetoStore, preProjetoId: number, professorId: number) {
        this.preProjetoState = apiState.loading

        const response: apiResponse<ApiError[]> = yield ppAprovarFuncionario(preProjetoId, professorId)
        this.preProjetoState = response.status

        if (response.status === apiState.done) {
            this.preProjeto.situacao = SituacaoDoPreProjeto.aprovado
        }

        if (response.status === apiState.error || response.status === apiState.badRequest) {
            this.preProjetoValidationError!.global = response.data!.flatMap(
                (err: ApiError) => err.errorMessages);
        }
    })
}
